/**
 * TrainerTermsGate
 *
 * Blocking consent gate for a trainer who has never accepted the Terms, or
 * who accepted an older version. Wraps the trainer app the way
 * PortalLayout's gate wraps the client portal.
 *
 * The summary below is deliberately NOT the client-facing one. A client is
 * told about exercise risk; a trainer is the business operator, so what
 * matters to them is the professional-responsibility and client-data
 * obligations they are taking on. The full Terms are one click away — this
 * screen is a summary, never the agreement itself.
 *
 * Acceptance is persisted server-side (profiles.terms_accepted_at +
 * terms_version via the accept_trainer_terms RPC), so it survives reload,
 * other devices, and re-login. It reappears only when TERMS_VERSION changes,
 * which is the point: a new version means new terms they have not seen.
 */

import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { TERMS_URL, PRIVACY_URL, TERMS_EFFECTIVE_DATE } from "@/lib/legal/version";

interface TrainerTermsGateProps {
  /** Persists acceptance; resolves false if the write failed. */
  onAccept: () => Promise<boolean>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
    </div>
  );
}

export function TrainerTermsGate({ onAccept }: TrainerTermsGateProps) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!checked || saving) return;
    setSaving(true);
    const ok = await onAccept();
    if (!ok) {
      // Stay on the gate. Reporting success on a failed write would be the
      // worst outcome here: the trainer believes they accepted, and there is
      // no record of it.
      toast.error("Couldn't save your acceptance. Check your connection and try again.");
      setSaving(false);
    }
    // On success the gate unmounts — no need to reset state.
  }, [checked, saving, onAccept]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-6 sm:p-8 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-semibold">Before you continue</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              We've updated our Terms of Service (effective {TERMS_EFFECTIVE_DATE}).
              Please review and accept them to keep using TrainerPulse.
            </p>
          </div>

          <div className="space-y-4">
            <Section title="You are the fitness professional">
              TrainerPulse is software for running your business. It does not
              provide medical, nutritional, or fitness advice, and it does not
              supervise your programming. Decisions about what is safe and
              appropriate for a client remain yours.
            </Section>

            <Section title="Your clients' data">
              You are responsible for having the right to collect and store the
              client information you enter, including health and body metrics,
              and for handling it lawfully. We process it on your behalf as
              described in the Privacy Policy.
            </Section>

            <Section title="AI-assisted features">
              Some features draft content and surface suggestions using AI.
              Output can be wrong. You are responsible for reviewing anything
              generated on your behalf before it reaches a client.
            </Section>

            <Section title="Billing and payouts">
              Subscription terms, payment processing, and any client payments
              you collect through the platform are covered in the Terms,
              including refunds and cancellation.
            </Section>

            <Section title="Dispute resolution">
              The Terms include a binding arbitration clause and a class-action
              waiver that affect how disputes between us are resolved. Please
              read that section.
            </Section>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Terms of Service <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Privacy Policy <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="border-t pt-6 space-y-4">
            {/* Affirmative action, not a pre-ticked box or passive prose. The
                checkbox is what makes this a record of agreement rather than a
                notice we displayed. */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => setChecked(v === true)}
                className="mt-0.5"
                aria-label="I have read and agree to the Terms of Service and Privacy Policy"
              />
              <span className="text-sm text-foreground leading-relaxed">
                I have read and agree to the Terms of Service and Privacy Policy.
              </span>
            </label>

            <Button
              onClick={handleAccept}
              disabled={!checked || saving}
              className="w-full"
              size="lg"
            >
              {saving ? "Saving…" : "Agree and continue"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
